import { scaleLog, scaleTime } from 'd3-scale';
import type { ScaleLogarithmic, ScaleTime } from 'd3-scale';

/**
 * Declarative, DOM-free data shaping run *before* a chart encodes its rows.
 * Each transform is a small pure function; `applyTransforms` folds a pipeline
 * left-to-right. This lets an agent say "top 10 by revenue, descending" instead
 * of wrangling the data itself. No dependencies beyond the math here.
 */

export type Row = Record<string, unknown>;

export type Comparator = '==' | '!=' | '>' | '>=' | '<' | '<=' | 'in';

export type Transform =
  | { op: 'sort'; by: string; dir?: 'asc' | 'desc' }
  | { op: 'filter'; field: string; cmp: Comparator; value: unknown }
  | {
      op: 'topN';
      by: string;
      n: number;
      rest?: 'drop' | 'group-other';
      labelField?: string;
      otherLabel?: string;
    }
  | { op: 'aggregate'; groupBy: string[]; field: string; reducer: Reducer; as?: string }
  | { op: 'bin'; field: string; bins: number; as?: string; countAs?: string }
  | { op: 'rolling'; field: string; window: number; reducer: 'mean' | 'sum'; as?: string }
  | { op: 'pivot'; index: string; column: string; value: string };

export type Reducer = 'sum' | 'mean' | 'count' | 'min' | 'max' | 'median';

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

function compareValues(a: unknown, b: unknown): number {
  const an = typeof a === 'number' ? a : Number(a);
  const bn = typeof b === 'number' ? b : Number(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  return String(a).localeCompare(String(b));
}

function reduce(values: number[], reducer: Reducer): number {
  if (reducer === 'count') return values.length;
  if (values.length === 0) return 0;
  switch (reducer) {
    case 'sum':
      return values.reduce((s, v) => s + v, 0);
    case 'mean':
      return values.reduce((s, v) => s + v, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }
  }
}

const matches = (cell: unknown, cmp: Comparator, value: unknown): boolean => {
  switch (cmp) {
    case '==':
      return cell === value;
    case '!=':
      return cell !== value;
    case 'in':
      return Array.isArray(value) && value.includes(cell);
    case '>':
      return num(cell) > num(value);
    case '>=':
      return num(cell) >= num(value);
    case '<':
      return num(cell) < num(value);
    case '<=':
      return num(cell) <= num(value);
  }
};

function applyOne(rows: Row[], t: Transform): Row[] {
  switch (t.op) {
    case 'sort': {
      const dir = t.dir === 'desc' ? -1 : 1;
      // Stable sort preserving input order for equal keys.
      return rows
        .map((row, i) => ({ row, i }))
        .sort((a, b) => compareValues(a.row[t.by], b.row[t.by]) * dir || a.i - b.i)
        .map(({ row }) => row);
    }
    case 'filter':
      return rows.filter((r) => matches(r[t.field], t.cmp, t.value));
    case 'topN': {
      const ranked = [...rows].sort((a, b) => compareValues(b[t.by], a[t.by]));
      const head = ranked.slice(0, Math.max(0, t.n));
      if (t.rest !== 'group-other' || ranked.length <= t.n) return head;
      const tail = ranked.slice(t.n);
      const other: Row = { [t.by]: tail.reduce((s, r) => s + num(r[t.by]), 0) };
      if (t.labelField) other[t.labelField] = t.otherLabel ?? 'Other';
      return [...head, other];
    }
    case 'aggregate': {
      const groups = new Map<string, { keys: Row; values: number[] }>();
      for (const r of rows) {
        const key = t.groupBy.map((g) => String(r[g])).join(' ');
        let g = groups.get(key);
        if (!g) {
          g = { keys: Object.fromEntries(t.groupBy.map((k) => [k, r[k]])), values: [] };
          groups.set(key, g);
        }
        g.values.push(num(r[t.field]));
      }
      return [...groups.values()].map((g) => ({
        ...g.keys,
        [t.as ?? t.field]: reduce(g.values, t.reducer),
      }));
    }
    case 'bin': {
      const xs = rows.map((r) => num(r[t.field]));
      if (xs.length === 0 || t.bins < 1) return [];
      const min = Math.min(...xs);
      const max = Math.max(...xs);
      const width = (max - min) / t.bins || 1;
      const counts = new Array(t.bins).fill(0);
      for (const x of xs) {
        const idx = Math.min(t.bins - 1, Math.floor((x - min) / width));
        counts[idx] += 1;
      }
      const round = (n: number) => Math.round(n * 100) / 100;
      return counts.map((count, i) => ({
        [t.as ?? `${t.field}_bin`]: `${round(min + i * width)}–${round(min + (i + 1) * width)}`,
        [t.countAs ?? 'count']: count,
      }));
    }
    case 'rolling': {
      const out: Row[] = [];
      const win: number[] = [];
      for (const r of rows) {
        win.push(num(r[t.field]));
        if (win.length > t.window) win.shift();
        out.push({
          ...r,
          [t.as ?? `${t.field}_rolling`]: reduce(win, t.reducer === 'sum' ? 'sum' : 'mean'),
        });
      }
      return out;
    }
    case 'pivot': {
      const byIndex = new Map<string, Row>();
      const order: string[] = [];
      for (const r of rows) {
        const key = String(r[t.index]);
        let row = byIndex.get(key);
        if (!row) {
          row = { [t.index]: r[t.index] };
          byIndex.set(key, row);
          order.push(key);
        }
        row[String(r[t.column])] = r[t.value];
      }
      return order.map((k) => byIndex.get(k)!);
    }
  }
}

/** Fold a transform pipeline over rows, left to right. Pure; never mutates input. */
export function applyTransforms(rows: Row[], pipeline: Transform[]): Row[] {
  return pipeline.reduce((acc, t) => applyOne(acc, t), rows);
}

/** Log scale (d3) for positive quantitative axes. */
export function logScale(
  domain: [number, number],
  range: [number, number],
): ScaleLogarithmic<number, number> {
  return scaleLog().domain(domain).range(range);
}

/** Time scale (d3) over epoch-millisecond domains. */
export function timeScale(
  domain: [number, number],
  range: [number, number],
): ScaleTime<number, number> {
  return scaleTime().domain(domain).range(range);
}
